// Event Alert Banner - Shows upcoming events on homepage
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ChevronRight, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { getText } from '../utils/translations';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EventAlertBanner = () => {
  const { language } = useLanguage();
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if already dismissed this session
    const isDismissed = sessionStorage.getItem('event_alert_dismissed');
    if (isDismissed) {
      setDismissed(true);
      setLoading(false);
      return;
    }

    fetchUpcomingEvents();
  }, []);

  const fetchUpcomingEvents = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/upcoming-events`);
      if (response.ok) {
        const data = await response.json();
        if (data.alert_enabled && data.events && data.events.length > 0) {
          setUpcomingEvents(data.events);
        }
      }
    } catch (err) {
      console.error('Error fetching upcoming events:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem('event_alert_dismissed', 'true');
  };

  const getEventTitle = (event) => {
    if (language === 'ar') return event.title_ar || event.title_fr || event.title_en;
    if (language === 'en') return event.title_en || event.title_fr;
    return event.title_fr || event.title_en;
  };

  const getDaysLabel = (days) => {
    if (days === 0) {
      return getText(language, "Today!", "Aujourd'hui !", "اليوم!");
    }
    if (days === 1) {
      return getText(language, "Tomorrow!", "Demain !", "غداً!");
    }
    return getText(language, `In ${days} days`, `Dans ${days} jours`, `خلال ${days} أيام`);
  };

  const getBannerColor = (days) => {
    if (days === 0) return 'bg-red-600';
    if (days === 1) return 'bg-red-500';
    if (days <= 3) return 'bg-orange-500';
    return 'bg-blue-600';
  };

  if (loading || dismissed || upcomingEvents.length === 0) {
    // Return empty placeholder with same height to prevent CLS
    return null;
  }

  const nextEvent = upcomingEvents[0];
  const daysUntil = nextEvent.days_until;

  return (
    <div 
      className={`${getBannerColor(daysUntil)} text-white py-3 px-4 relative`}
      style={{ minHeight: '52px' }} /* Prevent CLS */
    >
      <div className="container mx-auto flex items-center justify-center gap-4">
        <Calendar className="w-5 h-5 flex-shrink-0 animate-pulse" />
        <div className="flex items-center gap-2 flex-wrap justify-center text-sm sm:text-base">
          <span className="font-bold">
            {getDaysLabel(daysUntil)}
          </span>
          <span className="hidden sm:inline">—</span>
          <span className="font-medium truncate max-w-[200px] sm:max-w-none">
            {getEventTitle(nextEvent)}
          </span>
        </div>
        <Link 
          to="/events" 
          className="flex items-center gap-1 bg-white/20 hover:bg-white/30 px-3 py-1 rounded-full text-sm font-medium transition-colors"
        >
          {getText(language, "See details", "Voir", "التفاصيل")}
          <ChevronRight className="w-4 h-4" />
        </Link>
        <button 
          onClick={handleDismiss}
          className="absolute right-4 top-1/2 -translate-y-1/2 p-1 hover:bg-white/20 rounded-full transition-colors"
          aria-label="Fermer"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default EventAlertBanner;
